'use client';

import styles from './channelCard.module.scss';
import IconMenuKabab from '@/assets/icons/icon-menu-kebab.svg';
import IconAudioPlayButtonFilledShadow from '@/assets/icons/icon-audio-play-button-filled-shadow.svg';
import cn from 'classnames';
import SXMLink from '@/shared-components/SXMLink';
import { Text } from '@/ui-components/atoms/Text/Text';
import { ImageWrapper } from '@/shared-components/ImageWrapper/ImageWrapper';
import Album from '@/ui-components/atoms/Album/Album';

export interface ChannelCardProps {
    radioImageUrl: string;
    radioImageAlt?: string;
    radioImageHeight: number | `${number}` | undefined;
    radioImageWidth: number | `${number}` | undefined;
    title: string;
    subTitle?: React.ReactNode;
    showAndScheduleLink?: string;
    showAndScheduleLinkText?: string;
    showsAndScheduleLinkAriaLabel?: string;
    showAndScheduleLinkTarget?: string;
    listenLink: string;
    listenLinkAriaLabel: string;
    borderColor: string;
    disableShadow?: boolean;
    allowTextSquash?: boolean;
    albumImageSrc: string;
    albumImageAlt?: string;
    songTitle: string;
    songArtist: string;
    className?: string;
    wrapperClass?: string;
    onKebabClick?: () => void;
    activeItem?: number;
    index?: number;
}

export default function ChannelCard({
    radioImageUrl,
    radioImageAlt = 'Radio Channel Image',
    radioImageWidth,
    radioImageHeight,
    title,
    subTitle,
    showAndScheduleLink,
    showAndScheduleLinkText,
    showsAndScheduleLinkAriaLabel,
    showAndScheduleLinkTarget,
    listenLink,
    listenLinkAriaLabel,
    borderColor,
    disableShadow = false,
    allowTextSquash = false,
    albumImageSrc,
    albumImageAlt,
    songArtist,
    songTitle,
    className,
    onKebabClick,
    activeItem,
    index,
    wrapperClass,
}: ChannelCardProps) {
    const isActive = activeItem !== undefined && index !== undefined && activeItem === index;

    const handleOnClick = () => {
        onKebabClick?.();
    };

    const wrapperClasses = cn(styles.channelCard, wrapperClass, {
        [styles.active]: isActive,
        [styles.disableShadow]: disableShadow,
        [styles.allowTextSquash]: allowTextSquash,
    });

    const componentClasses = cn(styles.contentWrapper, className);

    const listenLinkAttr = {
        href: listenLink,
        'aria-label': listenLinkAriaLabel,
        target: '_blank',
        dataCy: 'molecules-channel-card-listen-live-link',
    };

    const albumImage = (
        <ImageWrapper
            src={albumImageSrc}
            alt={albumImageAlt}
            optimizedHeight={136}
            optimizedWidth={136}
            className={styles.albumImage}
        />
    );

    const AlbumHtml = (
        <>
            <IconMenuKabab className={styles.kabab} />

            <div className={styles.albumWrapper} aria-hidden="true">
                <Album
                    showInfoOnHover={false}
                    imageHeight="136"
                    imageWidth="136"
                    image={albumImage}
                    artist={songArtist}
                    songTitle={songTitle}
                    targetClass={styles.albumCover}
                />
            </div>
        </>
    );

    const ColorBorderHtml = (
        <>
            <div style={{ backgroundColor: borderColor }} className={styles.borderBackground}></div>
            <div className={styles.listenLiveText}>listen live</div>
            <IconAudioPlayButtonFilledShadow className={styles.playIcon} />
        </>
    );

    return (
        <div className={wrapperClasses} data-channelcard data-cy="molecules-channel-card-wrapper">
            <div className={componentClasses}>
                <div className={styles.radioInfoContainer}>
                    <SXMLink
                        className={styles.radioImageLink}
                        href={showAndScheduleLink || ''}
                        aria-label={showsAndScheduleLinkAriaLabel || ''}
                        dataCy="molecules-channel-card-link"
                    >
                        <ImageWrapper
                            optimizedWidth={radioImageWidth ? +radioImageWidth : 104}
                            optimizedHeight={radioImageHeight ? +radioImageHeight : 50}
                            className={styles.radioImage}
                            src={radioImageUrl}
                            alt={radioImageAlt}
                        />
                        <div className={styles.radioImageBackground}></div>
                    </SXMLink>

                    <div className={styles.radioTextInfo}>
                        <Text tag="p">{title}</Text>
                        <Text tag="p">{subTitle}</Text>
                        {showAndScheduleLink && (
                            <SXMLink
                                href={showAndScheduleLink}
                                aria-label={showsAndScheduleLinkAriaLabel}
                                target={showAndScheduleLinkTarget}
                                dataCy="molecules-channel-card-shows-and-schedule-link"
                            >
                                {showAndScheduleLinkText}
                            </SXMLink>
                        )}
                    </div>
                </div>

                <div className={styles.albumContainerMobile}>
                    <button onClick={handleOnClick} className={styles.button}>
                        {AlbumHtml}
                    </button>

                    <SXMLink className={styles.colorBorder} {...listenLinkAttr} data-player-link={''}>
                        {ColorBorderHtml}
                    </SXMLink>
                </div>

                <SXMLink className={styles.albumContainerDesktop} {...listenLinkAttr} data-player-link={''}>
                    <div className={styles.button}>{AlbumHtml}</div>

                    <div className={styles.colorBorder}>{ColorBorderHtml}</div>
                </SXMLink>
            </div>

            <div className={styles.shadow}>
                <div className={styles.desktopColorBorder} style={{ backgroundColor: borderColor }}></div>
            </div>
        </div>
    );
}
